import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    Tooltip
} from '@mui/material';
import { Add, Delete, VpnKey } from '@mui/icons-material';
import { activationService } from '../services/api';
import { copyTextToClipboard, promptManualCopy } from '../utils/clipboard';
import PageHeader from '../components/ui/PageHeader';

const ActivationPage: React.FC = () => {
    const [tokens, setTokens] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [openGenerate, setOpenGenerate] = useState(false);
    const [generateCount, setGenerateCount] = useState(1);
    const [tokenToDelete, setTokenToDelete] = useState<any | null>(null);
    const [deletingToken, setDeletingToken] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const fetchTokens = async () => {
        setLoading(true);
        try {
            const data = await activationService.getTokens();
            setTokens(data);
        } catch (err) {
            console.error(err);
            setError('Impossibile caricare i token. Riprova.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTokens();
    }, []);

    const handleGenerate = async () => {
        try {
            await activationService.generateTokens(generateCount);
            setOpenGenerate(false);
            setSuccess('Token generati con successo');
            fetchTokens();
        } catch (err) {
            console.error(err);
            setError('Impossibile generare i token. Riprova.');
        }
    };

    const canDeleteToken = (token: any) => {
        return token.status !== 'USED' && !token.usedAt && !token.usedBy && !token.machine;
    };

    const handleDeleteToken = async () => {
        if (!tokenToDelete) return;

        setDeletingToken(true);
        try {
            await activationService.deleteToken(tokenToDelete.id);
            setTokenToDelete(null);
            fetchTokens();
        } catch (err: any) {
            console.error(err);
            const message = err?.response?.data?.error?.message || 'Impossibile eliminare il token. Riprova.';
            setError(message);
        } finally {
            setDeletingToken(false);
        }
    };

    const copyToClipboard = async (text: string) => {
        const copied = await copyTextToClipboard(text);
        if (copied) {
            setSuccess('Codice copiato negli appunti');
            return;
        }
        promptManualCopy(text, 'il codice');
        setSuccess('Clipboard non disponibile: usa copia manuale');
    };

    return (
        <Box>
            <PageHeader title="Token attivazione macchine" subtitle="Genera i codici che i rivenditori utilizzano per registrare nuove macchine. Ogni token può essere utilizzato una sola volta."
                actions={<Button
                    variant="contained"
                    color="primary"
                    startIcon={<Add />}
                    onClick={() => setOpenGenerate(true)}
                >
                    Genera Nuovi Token
                </Button>} />
            <Alert severity="info" variant="outlined" sx={{ mb: 3 }}>I token servono a registrare una macchina. Le licenze di NextCut, NextFrame, NextDrill e NextCloud si gestiscono nella sezione <strong>Licenze software</strong>. Per NextControl apri la scheda della macchina.</Alert>

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Codice Attivazione</TableCell>
                            <TableCell>Stato</TableCell>
                            <TableCell>Creato Da</TableCell>
                            <TableCell>Data Creazione</TableCell>
                            <TableCell>Usato Da (Reseller)</TableCell>
                            <TableCell>Macchina Associata</TableCell>
                            <TableCell>Data Utilizzo</TableCell>
                            <TableCell align="right">Azioni</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {tokens.map((token) => (
                            <TableRow key={token.id}>
                                <TableCell>
                                    <Button
                                        startIcon={<VpnKey />}
                                        onClick={() => copyToClipboard(token.code)}
                                        size="small"
                                        sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}
                                    >
                                        {token.code}
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={{ AVAILABLE: 'Disponibile', USED: 'Utilizzato', REVOKED: 'Revocato' }[token.status as string] || token.status}
                                        color={token.status === 'AVAILABLE' ? 'success' : 'default'}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>{token.generatedBy?.displayName || 'N/A'}</TableCell>
                                <TableCell>{new Date(token.createdAt).toLocaleDateString()}</TableCell>
                                <TableCell>{token.usedBy?.displayName || '-'}</TableCell>
                                <TableCell>
                                    {token.machine ? `${token.machine.name} (${token.machine.serialNumber})` : '-'}
                                </TableCell>
                                <TableCell>
                                    {token.usedAt ? new Date(token.usedAt).toLocaleDateString() : '-'}
                                </TableCell>
                                <TableCell align="right">
                                    {canDeleteToken(token) ? (
                                        <Tooltip title="Elimina token non usato">
                                            <IconButton
                                                color="error"
                                                size="small"
                                                onClick={() => setTokenToDelete(token)}
                                            >
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    ) : (
                                        '-'
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                        {tokens.length === 0 && !loading && (
                            <TableRow>
                                <TableCell colSpan={8} align="center">
                                    Nessun token trovato. Generane uno nuovo.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={openGenerate} onClose={() => setOpenGenerate(false)}>
                <DialogTitle>Genera Nuovi Token</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Quantità"
                        type="number"
                        fullWidth
                        value={generateCount}
                        onChange={(e) => setGenerateCount(Math.min(50, Math.max(1, parseInt(e.target.value) || 1)))}
                        inputProps={{ min: 1, max: 50 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenGenerate(false)}>Annulla</Button>
                    <Button onClick={handleGenerate} variant="contained">Genera</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!tokenToDelete} onClose={() => !deletingToken && setTokenToDelete(null)}>
                <DialogTitle>Conferma Eliminazione</DialogTitle>
                <DialogContent>
                    Eliminare il token "{tokenToDelete?.code}"? Questa azione e&apos; irreversibile.
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTokenToDelete(null)} disabled={deletingToken}>
                        Annulla
                    </Button>
                    <Button
                        onClick={handleDeleteToken}
                        color="error"
                        variant="contained"
                        disabled={deletingToken}
                    >
                        Elimina
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ActivationPage;
